import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaSearch, FaFilter, FaTimes, FaHome } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { propertyApi } from '../services/api';
import './Properties.css';

const PROPERTY_TYPES = [
  { value: '', label: 'Todos los tipos' },
  { value: 'CASA', label: 'Casa' },
  { value: 'APARTAMENTO', label: 'Apartamento' },
  { value: 'TERRENO', label: 'Terreno' },
  { value: 'COMERCIAL', label: 'Local Comercial' },
  { value: 'OFICINA', label: 'Oficina' },
];

const Properties = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    type: searchParams.get('type') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    minBedrooms: searchParams.get('minBedrooms') || '',
    city: searchParams.get('city') || '',
  });

  const loadProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchParams.get('q')) params.q = searchParams.get('q');
      if (searchParams.get('type')) params.type = searchParams.get('type');
      if (searchParams.get('minPrice')) params.minPrice = searchParams.get('minPrice');
      if (searchParams.get('maxPrice')) params.maxPrice = searchParams.get('maxPrice');
      if (searchParams.get('minBedrooms')) params.minBedrooms = searchParams.get('minBedrooms');
      if (searchParams.get('city')) params.city = searchParams.get('city');

      const res = await propertyApi.getAll(params);
      setProperties(res.data?.data || []);
    } catch (e) {
      console.error('Error cargando propiedades:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ q: '', type: '', minPrice: '', maxPrice: '', minBedrooms: '', city: '' });
    setSearchParams({});
  };

  const hasFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="properties-page">
      <Navbar />

      <div className="properties-page__hero">
        <div className="properties-page__hero-content">
          <h1>Propiedades Disponibles</h1>
          <p>Encuentra tu propiedad ideal entre nuestra selección exclusiva</p>
        </div>
      </div>

      <div className="properties-page__body container">
        {/* Barra de búsqueda */}
        <div className="search-bar">
          <form onSubmit={handleSearch} className="search-bar__form">
            <div className="search-bar__input">
              <FaSearch />
              <input
                type="text"
                placeholder="Buscar por nombre, ciudad o dirección..."
                value={filters.q}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              />
            </div>

            <select
              value={filters.type}
              onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
              className="search-bar__select"
            >
              {PROPERTY_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <button type="submit" className="btn btn--navy">Buscar</button>

            <button
              type="button"
              className={`btn btn--outline-navy ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filtros
            </button>

            {hasFilters && (
              <button type="button" className="btn-clear" onClick={clearFilters}>
                <FaTimes /> Limpiar
              </button>
            )}
          </form>

          {/* Filtros avanzados */}
          {showFilters && (
            <div className="advanced-filters">
              <div className="advanced-filters__grid">
                <div className="filter-group">
                  <label>Precio mínimo (USD)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={filters.minPrice}
                    onChange={e => setFilters(f => ({ ...f, minPrice: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label>Precio máximo (USD)</label>
                  <input
                    type="number"
                    placeholder="Sin límite"
                    value={filters.maxPrice}
                    onChange={e => setFilters(f => ({ ...f, maxPrice: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label>Habitaciones mínimas</label>
                  <select
                    value={filters.minBedrooms}
                    onChange={e => setFilters(f => ({ ...f, minBedrooms: e.target.value }))}
                  >
                    <option value="">Cualquiera</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    placeholder="Ej: San Salvador"
                    value={filters.city}
                    onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                  />
                </div>
              </div>
              <div className="advanced-filters__actions">
                <button className="btn btn--navy" onClick={handleSearch}>
                  Aplicar filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Header resultados + toggles */}
        <div className="results-header">
          <div className="results-count">
            {loading ? 'Buscando...' : `${properties.length} propiedad${properties.length !== 1 ? 'es' : ''} encontrada${properties.length !== 1 ? 's' : ''}`}
          </div>

        </div>

        {/* Resultados */}
        {loading ? (
          <div className="properties-grid">
            {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton-card" style={{height:'340px'}} />)}
          </div>
        ) : properties.length === 0 ? (
          <div className="no-results">
            <div className="no-results__icon"><FaHome /></div>
            <h3>No se encontraron propiedades</h3>
            <p>Intenta con otros criterios de búsqueda</p>
            <button className="btn btn--navy" onClick={clearFilters}>
              Ver todas las propiedades
            </button>
          </div>
        ) : (
          <div className="properties-grid">
            {properties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Properties;
